import React, { useState, createContext, useContext, useEffect, useMemo } from 'react';
import { getEntityLink, timestamp, useRedirect, useVisibilityChange } from '../../Services/helpers';
import Callback from '../../Services/Callback';
import { ulid } from 'ulid';
import API from '../../Services/API';
import { useSession } from '../Session';
import Build from '../../Data/Build';
import ServiceWorker from '../../Services/ServiceWorker';
import { scrollDirection } from '../Help';
import { useToasts } from '../Toasts';

const MobileContext = createContext();

export const useMobileContext = () => {
    return useContext(MobileContext);
};

const receiveMessageCallback = new Callback();

export const Provider = ({ children }) => {
    const {go, Redirect} = useRedirect();
    const [deviceToken, setDeviceToken] = useState(null);
    const [notificationsEnabled, setNotificationsEnabled] = useState(null);
    const isMobile = !!window.ReactNativeWebView;
    const {notificationBadge, setDeviceToken: setSessionDeviceToken, showRedesign, showDarkMode} = useSession();
    const [workerStatus, setWorkerStatus] = useState(ServiceWorker.status);
    const [lastUpdateCheck, setLastUpdateCheck] = useState(null);
    const [downloadingUpdate, setDownloadingUpdate] = useState(false);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [latestAvailableBuild, setLatestAvailableBuild] = useState(null);
    const {showError} = useToasts();
    const [navHiddenRequesters, setNavHiddenRequesters] = useState([]);
    const [statusBarColourStack, setStatusBarColourStack] = useState(new Map());
    const [safeAreaInsets, setSafeAreaInsets] = useState(null);

    const statusBarColour = useMemo(() => {
        const colours = Array.from(statusBarColourStack.values());
        return colours.filter(Boolean).pop() ?? null;
    }, [statusBarColourStack]);

    const [appVersion, isIos, isAndroid] = useMemo(() => {
        const isIos = /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
        const isAndroid = /Android/.test(window.navigator.userAgent);

        if (!isMobile) {
            return [null, isIos, isAndroid];
        }

        return [
            window.navigator.userAgent.match(/SoapyApp\/([0-9.]+)/)?.[1] ?? null,
            isIos,
            isAndroid
        ];
    }, [isMobile]);

    useEffect(() => {
        register();

        ServiceWorker.onStatusChange(setWorkerStatus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setNotificationsBadge(notificationBadge);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationBadge]);

    useEffect(() => {
        setUserAgentFlags();
        setUISettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRedesign, showDarkMode, statusBarColour]);

    const register = () => {
        registerWithNative();
    }

    const registerWithNative = () => {
        if (!isMobile) {
            return;
        }

        // this.request('resetNotifications')
        //     .then(perms => console.log('Reset permissions', perms));

        window.receiveMessageFromMobile = (json) => {
            const data = JSON.parse(json);
            receiveMessageCallback.invoke(data)
        };

        registerListeners();
        request('ready', {build: Build}).then(res => {
            console.log('Ready acknowledged', res);
            getNotificationStatus();
            setUserAgentFlags();
            setUISettings();
        });
    }

    const setUserAgentFlags = () => {
        const flagStr = [
            workerStatus.enabled ? 'X' : '',
            showRedesign ? 'R' : '',
            showDarkMode ? 'D' : '',
        ].filter(Boolean).join('');

        API.setFlags(flagStr);

        if (!isMobile) {
            return;
        }

        return request(
            'setUserAgentFlags',
            {
                flags: flagStr
            }
        );
    }

    const setStatusBarColour = (identifier, colour) => {
        setStatusBarColourStack(prev => {
            const next = new Map(prev);
            if (colour === null || colour === undefined) {
                next.delete(identifier);
            } else {
                next.set(identifier, colour);
            }
            return next;
        });
    }

    const setUISettings = () => {
        request('setShowRedesign', {value: showRedesign ? 'true' : 'false'});
        request('setShowDarkMode', {value: showDarkMode ? 'true' : 'false'});
        request('setStatusBarColour', {value: statusBarColour});
    }

    const registerListeners = () => {
        addListener((msg) => {
            if (!msg || !msg?.event) {
                return;
            }

            switch (msg.event) {
                case 'redirect':
                    handleRedirect(msg.url);
                    // Tell the mobile app we've handled the URL so it stops trying to load it
                    request('urlHandled', {url: msg.url});
                    break;

                case 'scrollToTop':
                    if (window.scrollHook) {
                        window.scrollHook.invoke(scrollDirection.TOP);
                    }
                    break;

                case 'notificationsRegistered':
                    console.log('Notifications registered', msg.data)
                    setDeviceToken(msg.data.token);
                    setSessionDeviceToken(msg.data.token);

                    if (API.isAdminSwitchedBusiness() === false) {
                        API.post('/notifications/registerDevice', {
                            deviceToken: msg.data.token,
                            platform: msg.data.os ?? window.navigator.platform
                        });
                    }
                    break;

                case 'notificationSelected':
                    if (msg?.data?.entity?.id) {
                        go(getEntityLink(msg.data.entity.model, msg.data.entity.id));
                    }
                    break;

                case 'Hello':
                    // Left this debug in the mobile app, ffs (remove on next mobile update)
                    break;

                case 'twilio.callInvite':
                    console.log('Twilio incoming call invite', msg.data);
                    break;

                case 'twilio.cancelledCallInvite':
                    console.log('Twilio call invite cancelled', msg.data);
                    break;

                case 'twilio.callAccepted':
                    console.log('Twilio call accepted', msg.data);
                    break;

                case 'twilio.callRejected':
                    console.log('Twilio call rejected');
                    break;

                case 'twilio.registered':
                    console.log('Twilio registered for incoming calls');
                    break;

                case 'twilio.unregistered':
                    console.log('Twilio unregistered');
                    break;

                case 'twilio.outboundCallInitiated':
                    console.log('Twilio outbound call initiated', msg.data);
                    break;

                case 'twilio.callConnected':
                    console.log('Twilio call connected');
                    break;

                case 'twilio.callDisconnected':
                    console.log('Twilio call disconnected', msg.data);
                    break;

                case 'twilio.callRinging':
                    console.log('Twilio call ringing');
                    break;

                case 'twilio.callConnectFailure':
                    console.error('Twilio call connect failure', msg.data);
                    break;

                case 'twilio.callReconnecting':
                    console.log('Twilio call reconnecting', msg.data);
                    break;

                case 'twilio.callReconnected':
                    console.log('Twilio call reconnected');
                    break;

                case 'twilio.callQualityWarnings':
                    console.warn('Twilio call quality warnings', msg.data);
                    break;

                case 'safeAreaInsets':
                    if (msg?.data) {
                        setSafeAreaInsets(msg.data);
                    }
                    break;

                default:
                    console.warn('Unhandled event', msg);
                    break;
            }
        })
    }

    const postMessage = (data) => {
        if (!isMobile) {
            return;
        }

        console.log('Posting message', data);
        window.ReactNativeWebView.postMessage(JSON.stringify(data));
    }

    const request = (request, data) => {
        return new Promise((resolve) => {
            const requestId = ulid();

            let _removeListener;
            const listener = (data) => {
                if (data.requestId !== requestId) {
                    return;
                }

                resolve(data.result);
                _removeListener();
            }

            _removeListener = addListener(listener);

            const payload = Object.assign(
                {
                    requestId,
                    request,
                },
                data ?? {}
            );

            postMessage(payload);
        });
    }

    const addListener = (listener) => {
        return receiveMessageCallback.listen(listener);
    }

    const removeListener = (listener) => {
        return receiveMessageCallback.remove(listener);
    }

    const handleRedirect = (url) => {
        if (!url) {
            return;
        }

        if (url.indexOf('gspy.uk') > -1) {
            const code = url.replace('https://gspy.uk/', '');
            go(`/redirect-short-link/${code}`);
            return;
        }

        const cleanedUrl = url.replace(`${window.location.protocol}//${window.location.host}`, '').replace('https://app.getsoapy.com', '');
        go(cleanedUrl);
    }

    const setNotificationsBadge = (number) => {
        // Typo in the native code, remove the double t once the fix is out :facepalm:
        return request('settNotificationBadge', { number });
    }

    const getNotificationStatus = () => {
        request('notificationsStatus').then(res => {
            console.log('Notification status', res);
            setNotificationsEnabled(res.alert)

            // If notifications are enabled, request them anyway
            // This won't show anything to the user but will ensure the
            // Device ID is picked up, in case it was missed before
            if (res.alert) {
                requestNotifications();
            }
        });
    }

    const requestNotifications = () => {
        return request('requestNotifications').then(res => setNotificationsEnabled(res.alert));
    }

    const installWorker = async (progressCallback) => {
        await ServiceWorker.install(progressCallback);
        await setUserAgentFlags();
    }

    const uninstallWorker = async () => {
        await ServiceWorker.uninstall();
        await setUserAgentFlags();
    }

    const updateWorker = async (progressCallback) => {
        await ServiceWorker.update(progressCallback);
        await setUserAgentFlags();
    }

    const installUpdate = async () => {
        if (!updateAvailable) {
            return;
        }

        if (!isMobile || !(appVersion && appVersion >= '2.2.0')) {
            window.location.reload(true);
            return;
        }

        const rnd = Math.round(Math.random() * 100000).toString(16);
        try {
            request('installUpdate', {
                build: `${latestAvailableBuild}-${rnd}`,
                url: `${window.location.protocol}//${window.location.host}/${latestAvailableBuild}-${rnd}`
            });
        } catch (error) {
            console.error('Failed to install update:', error);
            showError('Update failed', 'Please try again later.');
        }
    };

    const hideNav = (identifier) => {
        setNavHiddenRequesters(current => {
            if (current.includes(identifier)) {
                return current;
            }
            return [...current, identifier];
        });
    };

    const showNav = (identifier) => {
        setNavHiddenRequesters(current => current.filter(id => id !== identifier));
    };

    const checkForUpdates = async () => {
        fetch('/build.txt').then(result => {
            result.text().then(async serverBuild => {
                const isOutOfDate = serverBuild.trim() > Build;
                setLatestAvailableBuild(serverBuild.trim());
                setLastUpdateCheck(timestamp());

                if (isOutOfDate) {
                    if (isMobile) {
                        setDownloadingUpdate(true);
                        await updateWorker();
                        setDownloadingUpdate(false);
                        setUpdateAvailable(true);
                    } else {
                        setUpdateAvailable(true);
                    }
                }
            })
        })
    }

    useEffect(() => {
        const timeout = setTimeout(() => {
            checkForUpdates();
        }, 60 * 15 * 1000);

        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useVisibilityChange(() => {
        if (lastUpdateCheck < (timestamp() - (60 * 15))) {
            checkForUpdates();
        }
    }, true);

    const providerValues = {
        deviceToken,
        addListener,
        removeListener,
        isMobile,
        isIos,
        isAndroid,
        platform: isMobile ? (isIos ? 'iOS' : 'Android') : null,
        request,
        register,
        registerListeners,
        postMessage,
        setNotificationsBadge,
        notificationsEnabled,
        requestNotifications,
        appVersion,
        isVersionAtLeast: (version) => appVersion && appVersion >= version,
        checkForUpdates,
        workerStatus,
        installWorker,
        uninstallWorker,
        updateWorker,
        downloadingUpdate,
        updateAvailable,
        latestAvailableBuild,
        installUpdate,
        isNavHidden: navHiddenRequesters.length > 0,
        hideNav,
        showNav,
        statusBarColour,
        setStatusBarColour,
        safeAreaInsets,
    };

    return (
        <MobileContext.Provider value={providerValues}>
            {children}
            <Redirect />
        </MobileContext.Provider>
    );
};
